<template>
  <div class="readiness-evolution-chart">
    <div class="readiness-evolution-chart__header">
      <div class="readiness-evolution-chart__title">
        {{ chartTitle }}
      </div>
      <div class="readiness-evolution-chart__toggle">
        <button
          v-for="v in ['7 weeks', '7 months', '7 quarters']"
          :key="v"
          class="readiness-evolution-chart__toggle-btn"
          :class="{ 'readiness-evolution-chart__toggle-btn--active': currentView === v }"
          @click="currentView = v"
        >
          {{ v }}
        </button>
      </div>
    </div>

    <div class="readiness-evolution-chart__divider"></div>

    <div v-if="isEmpty" class="readiness-evolution-chart__empty-state">
      <div class="readiness-evolution-chart__info-icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#344054" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="readiness-evolution-chart__empty-text">
        No Readiness Data available yet
      </div>
    </div>

    <div class="readiness-evolution-chart__body">
      <div
        v-for="(bar, index) in chartData"
        :key="index"
        class="readiness-evolution-chart__bar-container"
      >
        <div class="readiness-evolution-chart__bar-value">
          {{ bar.value !== null ? Math.round(bar.value) + '%' : '-' }}
        </div>
        <div class="readiness-evolution-chart__bar-box">
          <div class="readiness-evolution-chart__bar-background">
            <div
              class="readiness-evolution-chart__bar-empty"
              :class="{ 'readiness-evolution-chart__bar-empty--opacity': bar.value === null }"
              :style="{ height: getEmptyHeight(bar.value) }"
            ></div>
            <div
              v-if="bar.value !== null && bar.value > 0"
              class="readiness-evolution-chart__bar-fill"
              :class="getColorClass(bar.value)"
              :style="{ height: getBarHeight(bar.value) }"
            ></div>
          </div>
        </div>
        <div class="readiness-evolution-chart__bar-label">
          {{ bar.label }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
  today: {
    type: [Number, Object],
    default: null
  },
  today_month_minus_1: {
    type: [Number, Object],
    default: null
  },
  today_month_minus_2: {
    type: [Number, Object],
    default: null
  },
  today_month_minus_3: {
    type: [Number, Object],
    default: null
  },
  today_month_minus_4: {
    type: [Number, Object],
    default: null
  },
  today_month_minus_5: {
    type: [Number, Object],
    default: null
  },
  today_month_minus_6: {
    type: [Number, Object],
    default: null
  },
  view: {
    type: String,
    default: '7 weeks'
  },
  chartTitle: {
    type: String,
    default: 'Your Readiness over time'
  }
});

const currentView = ref(props.view);
const emit = defineEmits(['update:view']);

watch(() => props.view, (newVal) => {
  currentView.value = newVal;
});

watch(currentView, (newVal) => {
  emit('update:view', newVal);
});

const isEmpty = computed(() => {
  return [
    props.today,
    props.today_month_minus_1,
    props.today_month_minus_2,
    props.today_month_minus_3,
    props.today_month_minus_4,
    props.today_month_minus_5,
    props.today_month_minus_6
  ].every(v => v === null);
});

const getBarHeight = (value) => {
  if (value === null) return '0%';
  const percentage = (value / 150) * 100;
  return `${Math.min(percentage, 100)}%`;
};

const getEmptyHeight = (value) => {
  if (value === null) return '100%';
  const percentage = 100 - (Math.min(value, 150) / 150 * 100);
  return `${percentage}%`;
};

const getColorClass = (value) => {
  if (value === null) return 'default-color';
  
  if (value >= 126) return 'readiness-first-color';
  if (value >= 101) return 'readiness-second-color';
  if (value >= 76) return 'readiness-third-color';
  if (value >= 50) return 'readiness-forth-color';
  if (value >= 26) return 'readiness-fifth-color';
  if (value >= 1) return 'readiness-sixth-color';
  return 'default-color';
};

const chartData = computed(() => {
  const rawData = [
    props.today_month_minus_6,
    props.today_month_minus_5,
    props.today_month_minus_4,
    props.today_month_minus_3,
    props.today_month_minus_2,
    props.today_month_minus_1,
    props.today
  ];

  const now = new Date();
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return rawData.map((propValue, index) => {
    const offset = 6 - index;
    let label = '';
    let value = null;

    // Determine the value based on currentView and prop type
    if (propValue !== null && typeof propValue === 'object') {
      if (currentView.value === '7 weeks') value = propValue.weeks ?? propValue.week ?? null;
      else if (currentView.value === '7 months') value = propValue.months ?? propValue.month ?? null;
      else if (currentView.value === '7 quarters') value = propValue.quarters ?? propValue.quarter ?? null;
      
      if (value === null) value = propValue.value ?? null;
    } else {
      value = propValue;
    }

    if (currentView.value === '7 weeks') {
      if (offset === 0) {
        label = 'Today';
      } else {
        label = `-${offset}w`;
      }
    } else if (currentView.value === '7 months') {
      if (offset === 0) {
        label = 'Today';
      } else {
        const d = new Date(now.getFullYear(), now.getMonth() - offset, 1);
        label = `${months[d.getMonth()]} ${d.getFullYear()}`;
      }
    } else if (currentView.value === '7 quarters') {
      if (offset === 0) {
        label = 'Today';
      } else {
        const d = new Date(now.getFullYear(), now.getMonth() - (offset * 3), 1);
        const quarter = Math.floor(d.getMonth() / 3) + 1;
        label = `Q${quarter}/${d.getFullYear()}`;
      }
    }

    return { value, label };
  });
});
</script>

<style scoped>
.readiness-evolution-chart {
  padding: 24px;
  border-radius: 12px;
  border: 1px solid #EAECF0;
  background: #FFF;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  display: flex;
  flex-direction: column;
  width: 100%;
}

.readiness-evolution-chart__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.readiness-evolution-chart__title {
  color: #101828;
  font-family: Inter;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

.readiness-evolution-chart__toggle {
  display: flex;
  border-radius: 8px;
  border: 1px solid #D0D5DD;
  overflow: hidden;
}

.readiness-evolution-chart__toggle-btn {
  padding: 10px 16px;
  background: #FFF;
  border: none;
  border-right: 1px solid #D0D5DD;
  color: #344054;
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
  transition: background 0.2s;
}

.readiness-evolution-chart__toggle-btn:last-child {
  border-right: none;
}

.readiness-evolution-chart__toggle-btn--active {
  background: #F9FAFB;
  color: #101828;
}

.readiness-evolution-chart__divider {
  height: 1px;
  background: #EAECF0;
  margin-bottom: 24px;
}

.readiness-evolution-chart__empty-state {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #FFF;
  margin-bottom: 5px;
}

.readiness-evolution-chart__empty-text {
  color: #344054;
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.readiness-evolution-chart__info-icon {
  border-radius: 8px;
  padding: 8px;
  display: flex;
  border: 1px solid #EAECF0;
  box-shadow: 0px 1px 2px 0px #1018280D;
}

.readiness-evolution-chart__body {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  height: 200px;
  padding: 0 20px;
}

.readiness-evolution-chart__bar-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 40px;
}

.readiness-evolution-chart__bar-value {
  color: #454459;
  font-family: Inter;
  font-size: 10px;
  font-weight: 400;
  line-height: 18px;
  margin-bottom: 8px;
  opacity: 0.5;
}

.readiness-evolution-chart__bar-box {
  height: 150px;
  width: 9px;
  margin-bottom: 8px;
}

.readiness-evolution-chart__bar-background {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.readiness-evolution-chart__bar-empty {
  width: 100%;
  background-color: rgba(201, 199, 196, 0.3);
  border-radius: 100px;
  transition: height 0.3s ease;
}

.readiness-evolution-chart__bar-empty--opacity {
  background-color: rgba(100, 100, 100, 0.3);
}

.readiness-evolution-chart__bar-fill {
  width: 100%;
  border-radius: 100px;
  transition: height 0.3s ease;
}

.readiness-evolution-chart__bar-label {
  color: #454459;
  font-family: Inter;
  font-size: 10px;
  font-weight: 400;
  line-height: 18px;
  white-space: nowrap;
  opacity: 0.5;
}

/* Colors from ReadinessMap.vue and ReadinessBar.vue */
.readiness-sixth-color { background-color: #FF0303; }
.readiness-fifth-color { background-color: #FFA500; }
.readiness-forth-color { background-color: #F9EA19; }
.readiness-third-color { background-color: #00B813; }
.readiness-second-color { background-color: #00A3FF; }
.readiness-first-color { background-color: #2E1EC1; }
.default-color { background-color: #EAECF0; }

</style>
